import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { api } from '../api/client';
const ASPECT_LIST = [
    { key: 'orig', label: 'Original' },
    { key: '1:1', label: '1:1' },
    { key: '4:3', label: '4:3' },
    { key: '3:2', label: '3:2' },
    { key: '16:9', label: '16:9' },
    { key: '9:16', label: '9:16' },
    { key: '3:4', label: '3:4' },
    { key: '2:3', label: '2:3' },
];
const WIDTH_PRESETS = [480, 960, 1280, 1920, 2400];
export default function MediaEditModal({ source, title, queueIndex, queueTotal, onSkipRest, onDone, onCancel, }) {
    // Build a stable URL for the image preview.
    const [objUrl, setObjUrl] = useState(null);
    useEffect(() => {
        if (source.kind === 'file') {
            const u = URL.createObjectURL(source.file);
            setObjUrl(u);
            return () => URL.revokeObjectURL(u);
        }
        // Cache-bust the remote URL so re-edit always sees the latest.
        const sep = source.item.url.includes('?') ? '&' : '?';
        setObjUrl(source.item.url + sep + 'v=' + Date.now());
    }, [source]);
    const [mode, setMode] = useState('crop');
    const [aspectKey, setAspectKey] = useState('orig');
    const [naturalDims, setNaturalDims] = useState(null);
    const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [focal, setFocal] = useState(() => source.kind === 'remote'
        ? { x: source.item.focal_x, y: source.item.focal_y }
        : { x: 0.5, y: 0.5 });
    const [maxWidthSelected, setMaxWidthSelected] = useState(null);
    const [maxWidthCustom, setMaxWidthCustom] = useState('');
    const [rotate, setRotate] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    // Probe natural dimensions of the loaded image.
    useEffect(() => {
        if (!objUrl)
            return;
        const img = new Image();
        img.onload = () => setNaturalDims({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => setError('Could not read image dimensions.');
        img.src = objUrl;
    }, [objUrl]);
    // Aspect ratio number from the selected key.
    const aspectNum = useMemo(() => {
        if (aspectKey === 'orig') {
            if (!naturalDims)
                return 16 / 9;
            // After 90/270° rotation, swap.
            return rotate === 90 || rotate === 270
                ? naturalDims.h / naturalDims.w
                : naturalDims.w / naturalDims.h;
        }
        const [a, b] = aspectKey.split(':').map(Number);
        return a / b;
    }, [aspectKey, naturalDims, rotate]);
    // Resolve the requested max width (custom input wins if non-empty).
    const maxWidth = useMemo(() => {
        if (maxWidthCustom.trim() !== '') {
            const n = parseInt(maxWidthCustom, 10);
            if (!isNaN(n) && n >= 64 && n <= 4000)
                return n;
            return null;
        }
        return maxWidthSelected;
    }, [maxWidthSelected, maxWidthCustom]);
    function rotateBy(deg) {
        setRotate(((rotate + deg) % 360));
    }
    // ESC closes (when not busy).
    useEffect(() => {
        function onKey(e) {
            if (e.key === 'Escape' && !busy)
                onCancel();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onCancel]);
    // Focal mode: click coords are read from the IMAGE's bounding rect, not the
    // surrounding stage — fixes the long-standing drift bug where clicks in the
    // letterboxed space outside the image registered as valid focal points.
    const focalImgRef = useRef(null);
    function onFocalClick(e) {
        const img = focalImgRef.current;
        if (!img)
            return;
        const r = img.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right ||
            e.clientY < r.top || e.clientY > r.bottom)
            return;
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        setFocal({ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) });
    }
    function buildOps() {
        const ops = {};
        if (rotate !== 0)
            ops.rotate = rotate;
        // Crop is applied only if we're in crop mode AND the cropper reported a
        // rect AND the rect isn't effectively the entire (post-rotation) image.
        if (mode === 'crop' && croppedAreaPixels) {
            const c = croppedAreaPixels;
            let postW = naturalDims?.w ?? 0;
            let postH = naturalDims?.h ?? 0;
            if (rotate === 90 || rotate === 270)
                [postW, postH] = [postH, postW];
            const isFull = postW > 0 && postH > 0 &&
                c.x <= 1 && c.y <= 1 &&
                Math.abs(c.width - postW) <= 2 &&
                Math.abs(c.height - postH) <= 2;
            if (!isFull) {
                ops.crop = {
                    x: Math.max(0, Math.round(c.x)),
                    y: Math.max(0, Math.round(c.y)),
                    w: Math.max(1, Math.round(c.width)),
                    h: Math.max(1, Math.round(c.height)),
                };
            }
        }
        if (maxWidth !== null)
            ops.max_width = maxWidth;
        // Focal point: always include in focal mode; in crop mode, include only
        // if it differs from the existing remote value (don't overwrite intent).
        const focalChanged = source.kind === 'file'
            ? (focal.x !== 0.5 || focal.y !== 0.5)
            : (Math.abs(focal.x - source.item.focal_x) > 1e-3 ||
                Math.abs(focal.y - source.item.focal_y) > 1e-3);
        if (mode === 'focal' || focalChanged) {
            ops.focal = { x: focal.x, y: focal.y };
        }
        return ops;
    }
    async function submit() {
        setBusy(true);
        setError(null);
        try {
            const ops = buildOps();
            if (source.kind === 'file') {
                const r = await api.uploadMedia(source.file, ops);
                onDone(r.item, r.warnings ?? []);
            }
            else {
                const r = await api.transformMedia(source.item.rel, ops);
                const updated = {
                    ...source.item,
                    width: r.width,
                    height: r.height,
                    focal_x: ops.focal ? ops.focal.x : source.item.focal_x,
                    focal_y: ops.focal ? ops.focal.y : source.item.focal_y,
                    url: source.item.url.split('?')[0] + '?v=' + r.cache_bust,
                    thumb_url: source.item.thumb_url.split('?')[0] + '?v=' + r.cache_bust,
                };
                onDone(updated, []);
            }
        }
        catch (e) {
            setError(e?.message ?? 'Save failed');
        }
        finally {
            setBusy(false);
        }
    }
    const queueLabel = (queueTotal && queueTotal > 1)
        ? `Editing ${(queueIndex ?? 0) + 1} of ${queueTotal}` : null;
    const sourceLabel = source.kind === 'file' ? source.file.name : source.item.rel;
    return (_jsx("div", { className: "modal-backdrop", onClick: (e) => { if (e.target === e.currentTarget && !busy)
            onCancel(); }, children: _jsxs("div", { className: "modal media-edit-modal", children: [_jsxs("div", { className: "modal-head", children: [_jsxs("div", { children: [_jsx("div", { className: "modal-title", children: title ?? (source.kind === 'file' ? 'Prepare upload' : 'Edit image') }), _jsxs("div", { className: "muted small", children: [sourceLabel, queueLabel ? ` · ${queueLabel}` : ''] })] }), _jsxs("div", { className: "modal-tabs", children: [_jsx("button", { className: mode === 'focal' ? 'tab active' : 'tab', onClick: () => setMode('focal'), disabled: busy, children: "Focal point" }), _jsx("button", { className: mode === 'crop' ? 'tab active' : 'tab', onClick: () => setMode('crop'), disabled: busy, children: "Crop" })] }), _jsx("button", { className: "ghost", onClick: onCancel, disabled: busy, children: "Close" })] }), _jsxs("div", { className: "modal-body media-edit-body", children: [error && _jsx("div", { className: "err small pad-y", children: error }), _jsxs("div", { className: "media-edit-stage", children: [!objUrl && _jsx("div", { className: "muted", children: "Loading\u2026" }), objUrl && mode === 'crop' && (_jsx(Cropper, { image: objUrl, crop: cropPos, zoom: zoom, rotation: rotate, aspect: aspectNum, onCropChange: setCropPos, onZoomChange: setZoom, onCropComplete: (_a, p) => setCroppedAreaPixels(p), objectFit: "contain", showGrid: true, restrictPosition: true, minZoom: 1, maxZoom: 8 })), objUrl && mode === 'focal' && (_jsx(FocalStage, { imgUrl: objUrl, focal: focal, rotate: rotate, imgRef: focalImgRef, onClick: onFocalClick }))] }), mode === 'crop' && (_jsxs("div", { className: "media-edit-controls", children: [_jsxs("div", { className: "control-row", children: [_jsx("span", { className: "control-label", children: "Aspect ratio" }), _jsx("div", { className: "pill-row", children: ASPECT_LIST.map((a) => (_jsx("button", { className: aspectKey === a.key ? 'pill active' : 'pill', onClick: () => setAspectKey(a.key), disabled: busy, children: a.label }, a.key))) })] }), _jsxs("div", { className: "control-row", children: [_jsx("span", { className: "control-label", children: "Zoom" }), _jsx("input", { type: "range", min: 1, max: 8, step: 0.05, value: zoom, onChange: (e) => setZoom(parseFloat(e.target.value)), disabled: busy, style: { flex: 1 } }), _jsx("button", { className: "ghost", onClick: () => { setZoom(1); setCropPos({ x: 0, y: 0 }); }, disabled: busy, children: "Fit" })] })] })), mode === 'focal' && (_jsx("div", { className: "media-edit-controls", children: _jsxs("div", { className: "control-row", children: [_jsx("span", { className: "control-label", children: "Focal point" }), _jsxs("span", { className: "muted small", children: ["x: ", focal.x.toFixed(3), ", y: ", focal.y.toFixed(3)] }), _jsx("button", { className: "ghost", onClick: () => setFocal({ x: 0.5, y: 0.5 }), disabled: busy, children: "Center" })] }) })), _jsxs("div", { className: "media-edit-controls", children: [_jsxs("div", { className: "control-row", children: [_jsx("span", { className: "control-label", children: "Rotate" }), _jsx("span", { className: "muted small", children: rotate === 0 ? 'None' : `${rotate}°` }), _jsx("button", { className: "ghost", onClick: () => rotateBy(90), disabled: busy, children: "\u21BB 90\u00B0" }), _jsx("button", { className: "ghost", onClick: () => rotateBy(180), disabled: busy, children: "\u21BB 180\u00B0" }), _jsx("button", { className: "ghost", onClick: () => rotateBy(270), disabled: busy, children: "\u21BB 270\u00B0" }), rotate !== 0 && (_jsx("button", { className: "ghost", onClick: () => setRotate(0), disabled: busy, children: "Reset" }))] }), _jsxs("div", { className: "control-row", children: [_jsx("span", { className: "control-label", children: "Max width" }), _jsxs("div", { className: "pill-row", children: [_jsx("button", { className: maxWidthSelected === null && maxWidthCustom === '' ? 'pill active' : 'pill', onClick: () => { setMaxWidthSelected(null); setMaxWidthCustom(''); }, disabled: busy, children: "Original" }), WIDTH_PRESETS.map((w) => (_jsx("button", { className: maxWidthSelected === w && maxWidthCustom === '' ? 'pill active' : 'pill', onClick: () => { setMaxWidthSelected(w); setMaxWidthCustom(''); }, disabled: busy, children: w }, w)))] }), _jsx("input", { type: "number", placeholder: "Custom", min: 64, max: 4000, value: maxWidthCustom, onChange: (e) => { setMaxWidthCustom(e.target.value); setMaxWidthSelected(null); }, disabled: busy, style: { width: 96 } }), _jsx("span", { className: "muted small", children: "px" })] })] })] }), _jsxs("div", { className: "modal-foot", children: [_jsx("span", { className: "muted small", children: naturalDims ? `Source: ${naturalDims.w}×${naturalDims.h}px` : '—' }), _jsxs("div", { className: "actions", children: [onSkipRest && (_jsx("button", { className: "ghost", onClick: onSkipRest, disabled: busy, children: "Skip editing for the rest" })), _jsx("button", { className: "ghost", onClick: onCancel, disabled: busy, children: "Cancel" }), _jsx("button", { onClick: submit, disabled: busy || !objUrl, children: busy ? 'Saving…' : (source.kind === 'file' ? 'Upload' : 'Apply') })] })] })] }) }));
}
function FocalStage({ imgUrl, focal, rotate, imgRef, onClick, }) {
    // Force a re-render when the image loads so the dot's position can be
    // computed from the rendered bounding rect.
    const [, setReady] = useState(false);
    return (_jsxs("div", { className: "media-edit-focal-stage", onClick: onClick, children: [_jsx("img", { ref: imgRef, src: imgUrl, alt: "", draggable: false, style: { transform: rotate ? `rotate(${rotate}deg)` : undefined }, onLoad: () => setReady(true) }), imgRef.current && (() => {
                const r = imgRef.current.getBoundingClientRect();
                const stage = imgRef.current.parentElement?.getBoundingClientRect();
                if (!stage)
                    return null;
                const left = (r.left - stage.left) + focal.x * r.width;
                const top = (r.top - stage.top) + focal.y * r.height;
                return _jsx("span", { className: "focal-dot", style: { left, top } });
            })()] }));
}
